/**
 * Tenant Portal Entry Point
 * ==========================
 * نقطة الدخول لبوابة الشركات
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '../lib/context/ThemeContext';
import { TenantAuthProvider, useTenantAuth } from './context/TenantAuthContext';
import { ToastProvider } from './context/ToastContext';
import { DialogProvider } from '../context/DialogContext';

// Layouts
import TenantLayout from './layouts/TenantLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import EmployeesPage from './pages/employees/EmployeesPage';
import DriversPage from './pages/drivers/DriversPage';
import DriverDetailPage from './pages/drivers/DriverDetailPage';
import PendingDriversPage from './pages/drivers/PendingDriversPage';
import DriverApplicationsPage from './pages/drivers/DriverApplicationsPage';
import DriversMapPage from './pages/drivers/DriversMapPage';
import ShipsPage from './pages/ships/ShipsPage';
import ShipDetailsPage from './pages/ships/ShipDetailsPage';
import AddShipPage from './pages/ships/AddShipPage';
import EditShipPage from './pages/ships/EditShipPage';
import VehiclesPage from './pages/vehicles/VehiclesPage';
import VehicleDetailPage from './pages/vehicles/VehicleDetailPage';
import AddVehiclePage from './pages/vehicles/AddVehiclePage';
import OrdersPage from './pages/orders/OrdersPage';
import SupportPage from './pages/support/SupportPage';
import PromotionsPage from './pages/promotions/PromotionsPage';
import MediaPage from './pages/media/MediaPage';
import SettingsPage from './pages/settings/SettingsPage';
import VehicleSettingsPage from './pages/settings/VehicleSettingsPage';
import ReportsPage from './pages/reports/ReportsPage';
import PricingRulesPage from './pages/pricing/PricingRulesPage';
import ShiftsPage from './pages/shifts/ShiftsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useTenantAuth();

  if (loading) {
    return (
      <div className="watrly-tenant-loading">
        <div className="watrly-tenant-loading__spinner" />
        <span>جاري التحميل...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/tenant/login" replace />;
  }

  return children;
};

// Tenant App Component
const TenantApp = () => {
  return (
    <ThemeProvider initialLocale="ar">
      <TenantAuthProvider>
        <ToastProvider>
          <DialogProvider>
          <BrowserRouter>
            <Routes>
              {/* Auth Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/tenant/login" element={<LoginPage />} />
                <Route path="/tenant/register" element={<RegisterPage />} />
              </Route>

              {/* Protected Tenant Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <TenantLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/tenant" element={<Navigate to="/tenant/dashboard" replace />} />
                <Route path="/tenant/dashboard" element={<DashboardPage />} />
                <Route path="/tenant/employees" element={<EmployeesPage />} />
                <Route path="/tenant/drivers" element={<DriversPage />} />
                <Route path="/tenant/drivers/pending" element={<PendingDriversPage />} />
                <Route path="/tenant/drivers/applications" element={<DriverApplicationsPage />} />
                <Route path="/tenant/drivers/map" element={<DriversMapPage />} />
                <Route path="/tenant/drivers/:id" element={<DriverDetailPage />} />
                <Route path="/tenant/ships" element={<ShipsPage />} />
                <Route path="/tenant/ships/add" element={<AddShipPage />} />
                <Route path="/tenant/ships/edit/:id" element={<EditShipPage />} />
                <Route path="/tenant/ships/:id" element={<ShipDetailsPage />} />
                <Route path="/tenant/vehicles" element={<VehiclesPage />} />
                <Route path="/tenant/vehicles/add" element={<AddVehiclePage />} />
                <Route path="/tenant/vehicles/:id" element={<VehicleDetailPage />} />
                <Route path="/tenant/orders" element={<OrdersPage />} />
                <Route path="/tenant/support" element={<SupportPage />} />
                <Route path="/tenant/promotions" element={<PromotionsPage />} />
                <Route path="/tenant/media" element={<MediaPage />} />
                <Route path="/tenant/reports" element={<ReportsPage />} />
                <Route path="/tenant/settings" element={<SettingsPage />} />
                <Route path="/tenant/settings/vehicles" element={<VehicleSettingsPage />} />
                <Route path="/tenant/pricing" element={<PricingRulesPage />} />
                <Route path="/tenant/shifts" element={<ShiftsPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
          </DialogProvider>
        </ToastProvider>
      </TenantAuthProvider>
    </ThemeProvider>
  );
};

export default TenantApp;
